/**
 * The page's console, relayed to the CRM preview frame (#REW-974).
 *
 * The CRM frames the page on the page's own domain, so it cannot read the
 * frame's console. In a scripts preview only, the page wraps `console.*` and
 * listens to uncaught errors and rejections, and posts every line up:
 *
 *   page → parent  { type: 'TAGADAPAY_CONSOLE', level, text, time, index, line }
 *
 * `index` is the draft script the line came from (its place in the list) and
 * `line` the merchant's line in it, when the stack names one; both null for
 * the page's own lines. Lines logged before the CRM answered the scripts
 * request are held, then sent once it did, or dropped when it never did.
 * The page's own console keeps every line: the wrappers call through.
 */

export const CONSOLE_MESSAGE = 'TAGADAPAY_CONSOLE';

export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error';

/** The draft script a line came from, and the merchant's line in it when known. */
export interface ConsoleSource {
  index: number;
  line: number | null;
}

export interface ConsoleLine {
  level: ConsoleLevel;
  text: string;
  time: number;
  index: number | null;
  line: number | null;
}

/** The script named by a stack, if any. */
export type AttributeStack = (stack: string | undefined) => ConsoleSource | null;

type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';

const METHODS: Record<ConsoleMethod, ConsoleLevel> = { log: 'log', info: 'info', warn: 'warn', error: 'error', debug: 'log' };

/** A page that logs in a loop stops being relayed there; its own console goes on. */
export const CONSOLE_MAX_LINES = 1000;
const MAX_TEXT = 4000;
const MAX_DEPTH = 3;

type ConsoleWindow = Pick<Window, 'parent' | 'addEventListener' | 'removeEventListener'> & {
  console?: Partial<Record<ConsoleMethod, (...args: unknown[]) => void>>;
};

interface Relay {
  win: ConsoleWindow;
  originals: Partial<Record<ConsoleMethod, (...args: unknown[]) => void>>;
  attribute: AttributeStack;
  held: ConsoleLine[];
  origin: string | null;
  count: number;
  inside: boolean;
  onError: (event: ErrorEvent) => void;
  onRejection: (event: PromiseRejectionEvent) => void;
}

let relay: Relay | null = null;

function describeElement(value: { tagName: string; id?: string; className?: unknown }): string {
  const id = value.id ? `#${value.id}` : '';
  const classes = typeof value.className === 'string' && value.className.trim() ? `.${value.className.trim().split(/\s+/).join('.')}` : '';
  return `<${value.tagName.toLowerCase()}${id}${classes}>`;
}

function isElement(value: object): value is { tagName: string; id?: string; className?: unknown } {
  return (value as { nodeType?: unknown }).nodeType === 1 && typeof (value as { tagName?: unknown }).tagName === 'string';
}

/** Plain data for JSON: no cycles, elements and functions named, past a depth summarised. */
function plain(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'function') return `ƒ ${value.name || 'anonymous'}()`;
  if (typeof value === 'bigint') return `${value.toString()}n`;
  if (typeof value === 'symbol' || typeof value === 'undefined') return String(value);
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (isElement(value)) return describeElement(value);
  if (seen.has(value)) return '[Circular]';
  if (depth >= MAX_DEPTH) return Array.isArray(value) ? `Array(${value.length})` : '{…}';
  seen.add(value);
  if (Array.isArray(value)) return value.map((item) => plain(item, depth + 1, seen));
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    try {
      out[key] = plain((value as Record<string, unknown>)[key], depth + 1, seen);
    } catch {
      out[key] = '[unreadable]';
    }
  }
  return out;
}

/** One console argument as the browser would roughly print it. */
export function formatConsoleArg(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack && value.stack.includes(value.message) ? value.stack : `${value.name}: ${value.message}`;
  if (value !== null && typeof value === 'object' && isElement(value)) return describeElement(value);
  if (value === null || typeof value !== 'object') {
    return typeof value === 'function' || typeof value === 'bigint' ? String(plain(value, 0, new WeakSet())) : String(value);
  }
  try {
    return JSON.stringify(plain(value, 0, new WeakSet()));
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/** The arguments of a console call as one line, `%s`-style substitutions applied and `%c` styles dropped. */
export function formatConsoleArgs(args: readonly unknown[]): string {
  const rest = [...args];
  let head = '';
  if (typeof rest[0] === 'string' && rest[0].includes('%')) {
    const format = rest.shift() as string;
    head = format.replace(/%([sdifoOc%])/g, (match, kind: string) => {
      if (kind === '%') return '%';
      if (rest.length === 0) return match;
      const value = rest.shift();
      if (kind === 'c') return '';
      if (kind === 'd' || kind === 'i') return String(Number.parseInt(String(value), 10));
      if (kind === 'f') return String(Number(value));
      return formatConsoleArg(value);
    });
  }
  const text = [head, ...rest.map(formatConsoleArg)].filter((part, at) => at > 0 || part !== '').join(' ');
  return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}…` : text;
}

/** The script a call came from: an Error argument's stack first, else the stack of the call itself. */
function sourceOf(attribute: AttributeStack, args: readonly unknown[], callStack: string | undefined): ConsoleSource | null {
  for (const arg of args) {
    if (arg instanceof Error) {
      const source = attribute(arg.stack);
      if (source) return source;
    }
  }
  return attribute(callStack);
}

function post(current: Relay, line: ConsoleLine): void {
  if (current.origin === null) {
    if (current.held.length < CONSOLE_MAX_LINES) current.held.push(line);
    return;
  }
  if (current.count >= CONSOLE_MAX_LINES) return;
  current.count += 1;
  const last = current.count === CONSOLE_MAX_LINES;
  const message = last
    ? { ...line, level: 'warn' as const, text: `Console limit of ${CONSOLE_MAX_LINES} lines reached: later lines are not shown here.`, index: null, line: null }
    : line;
  current.win.parent.postMessage({ type: CONSOLE_MESSAGE, ...message }, current.origin);
}

function record(current: Relay, level: ConsoleLevel, text: string, source: ConsoleSource | null): void {
  if (current.inside) return;
  current.inside = true;
  try {
    post(current, { level, text, time: Date.now(), index: source?.index ?? null, line: source?.line ?? null });
  } catch {
    // A line that cannot be sent is only missing from the CRM's box.
  } finally {
    current.inside = false;
  }
}

/**
 * Start relaying the page's console, holding the lines until the CRM answers.
 * Does nothing when a relay already runs or the window has no console.
 */
export function startConsoleRelay(win: ConsoleWindow, attribute: AttributeStack): void {
  if (relay || !win.console) return;
  const target = win.console;
  const current: Relay = {
    win,
    originals: {},
    attribute,
    held: [],
    origin: null,
    count: 0,
    inside: false,
    onError: (event) => {
      const error: unknown = event.error;
      const fromFile = attribute(event.filename ? `${event.filename}:${event.lineno}` : undefined);
      const source = fromFile ?? (error instanceof Error ? attribute(error.stack) : null);
      record(current, 'error', `Uncaught ${event.message || formatConsoleArg(error)}`, source);
    },
    onRejection: (event) => {
      const reason: unknown = event.reason;
      record(current, 'error', `Unhandled promise rejection: ${formatConsoleArg(reason)}`, reason instanceof Error ? attribute(reason.stack) : null);
    },
  };
  for (const method of Object.keys(METHODS) as ConsoleMethod[]) {
    const original = target[method];
    if (typeof original !== 'function') continue;
    current.originals[method] = original;
    target[method] = (...args: unknown[]) => {
      original.apply(target, args);
      if (current.inside) return;
      record(current, METHODS[method], formatConsoleArgs(args), sourceOf(attribute, args, new Error().stack));
    };
  }
  win.addEventListener('error', current.onError as EventListener);
  win.addEventListener('unhandledrejection', current.onRejection as EventListener);
  relay = current;
}

/** Give the console back and forget the held lines. */
export function stopConsoleRelay(): void {
  if (!relay) return;
  const { win, originals, onError, onRejection } = relay;
  if (win.console) Object.assign(win.console, originals);
  win.removeEventListener('error', onError as EventListener);
  win.removeEventListener('unhandledrejection', onRejection as EventListener);
  relay = null;
}

/**
 * The scripts request is over: with the CRM's origin, send the held lines and
 * every later one to it; without (nobody answered), stop relaying.
 */
export function settleConsoleRelay(origin: string | null): void {
  if (!relay) return;
  if (origin === null) {
    stopConsoleRelay();
    return;
  }
  const current = relay;
  current.origin = origin;
  const held = current.held;
  current.held = [];
  for (const line of held) post(current, line);
}
